import { AbstractService } from '@uniqoders/sdk';
import ClientTour from '@/api/objects/ClientTour';

export default class CaseService extends AbstractService {
  /**
   * Retrieves all Cases paginated
   * @protected
   * @param agency
   * @param params
   * @param paginate
   */
  public allPaginated(agency: string, params: any, paginate = true): Promise<any> {
    return this.request({
      method: 'get',
      url: this.buildPath('/{agency}/cases', { agency }),
      params,
      options: {
        paginate,
      },
    });
  }

  /**
   * Retrieves a specific Case
   * @param agency
   * @param clientTour
   * @protected
   */
  public retrieve(agency: string, clientTour: number): Promise<ClientTour> {
    return this.request({
      method: 'get',
      url: this.buildPath('/{agency}/case/{clientTour}', { agency, clientTour }),
    });
  }

  /**
   * Creates a single Case
   * @protected
   * @param agency
   * @param data
   */
  public create(agency: string, data: Record<string, any>): Promise<ClientTour> {
    return this.request({
      method: 'post',
      url: this.buildPath('/{agency}/case', { agency }),
      data,
    });
  }

  public createCollaborator(agency: string, data: Record<string, any>): Promise<ClientTour> {
    return this.request({
      method: 'post',
      url: this.buildPath('/{agency}/collaborator-case', { agency }),
      data,
    });
  }
  /**
   * Updates the specified Case
   * @param agency
   * @param clientTour
   * @param data
   * @protected
   */
  public update(agency: string, clientTour: string | number, data: Record<string, any>): Promise<ClientTour> {
    return this.request({
      method: 'patch',
      url: this.buildPath('/{agency}/case/{clientTour}', { agency, clientTour }),
      data,
    });
  }

  /**
   * Updates the specified Case
   * @param agency
   * @param clientTour
   * @param data
   * @protected
   */
  public updateInitialDate(
    agency: string,
    clientTour: string | number,
    data: Record<string, any>): Promise<ClientTour> {
    return this.request({
      method: 'patch',
      url: this.buildPath('/{agency}/case/{clientTour}/init-date', { agency, clientTour }),
      data,
    });
  }

  /**
   * Archives the specified Case
   * @protected
   * @param agency
   * @param clientTour
   */
  public archive(agency: string, clientTour: string | number): Promise<ClientTour> {
    return this.request({
      method: 'delete',
      url: this.buildPath('/{agency}/case/{clientTour}', { agency, clientTour }),
    });
  }

  /**
   * Restore the specified Case
   * @protected
   * @param agency
   * @param clientTour
   */
  public restore(agency: string, clientTour: string | number): Promise<ClientTour> {
    return this.request({
      method: 'patch',
      url: this.buildPath('/{agency}/case/{clientTour}/restore', { agency, clientTour }),
    });
  }

  /**
   * Deletes the specified Case
   * @protected
   * @param agency
   * @param clientTour
   */
  public delete(agency: string, clientTour: string | number): Promise<ClientTour> {
    return this.request({
      method: 'delete',
      url: this.buildPath('/{agency}/case/{clientTour}/force', { agency, clientTour }),
    });
  }

  /**
   * Assign user to the specified Case
   * @protected
   * @param agency
   * @param clientTour
   * @param data
   */
  public assignUser(agency: string, clientTour: string | number, data: any): Promise<ClientTour> {
    return this.request({
      method: 'post',
      url: this.buildPath('/{agency}/case/{clientTour}/assign', { agency, clientTour }),
      data,
    });
  }

  public unassignUser(agency: string, clientTour: string | number): Promise<any> {
    return this.request({
      method: 'post',
      url: this.buildPath('/{agency}/case/{clientTour}/unassign', { agency, clientTour }),
    });
  }

  public connectLuggage(agency: string, clientTour: string | number, tour: string | number): Promise<any> {
    return this.request({
      method: 'post',
      url: this.buildPath('/{agency}/case/{clientTour}/tour/{tour}/update-luggage-transfer', { agency, clientTour, tour }),
    });
  }

  /**
   * Retrieves all Cases paginated
   * @protected
   * @param agency
   * @param params
   * @param paginate
   */
  public allCasesPaginated(agency: string, params: any, paginate = true): Promise<any> {
    return this.request({
      method: 'get',
      url: this.buildPath('/v2/{agency}/cases', { agency }),
      params,
      options: {
        paginate,
      },
    });
  }

  /**
   * version of the case
   * @protected
   * @param agency
   * @param clientCase
   */
  public caseVersion(agency: string, clientCase: number): Promise<ClientTour> {
    return this.request({
      method: 'get',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/versions', { agency, clientCase })
    });
  }

    /**
   * Creates a single Case
   * @protected
   * @param agency
   * @param data
   */
    public createCase(agency: string, data: Record<string, any>): Promise<ClientTour> {
      return this.request({
        method: 'post',
        url: this.buildPath('/v2/{agency}/case/create', { agency }),
        data,
      });
    }

  /**
   * Retrieves a specific Case
   * @param agency
   * @param clientCase
   * @protected
   */
  public retrieveCase(agency: string, clientCase: number): Promise<ClientTour> {
    return this.request({
      method: 'get',
      url: this.buildPath('/v2/{agency}/case/{clientCase}', { agency, clientCase }),
    });
  }

  /**
   * Updates the basic info of a Case
   * @param agency
   * @param clientCaseId
   * @param data
   * @protected
   */
  public updateBasicInfoCase(agency: string, clientCaseId: number, data: Record<string, any>): Promise<ClientTour> {
    return this.request({
      method: 'put',
      url: this.buildPath('/v2/{agency}/case/{clientCaseId}', { agency, clientCaseId }),
      data
    });
  }

  /**
   * Updates the contact info of a Case
   * @param agency
   * @param clientCaseId
   * @param data
   * @protected
   */
  public updateContactInfo(agency: string, clientCaseId: number, data: Record<string, any>): Promise<ClientTour> {
    return this.request({
      method: 'put',
      url: this.buildPath('/v2/{agency}/case/{clientCaseId}/update-contact-text', { agency, clientCaseId }),
      data
    });
  }

  /**
   * Updates the translations of a Case
   * @param agency
   * @param clientCaseId
   * @param data
   * @protected
   */
  public updateTranslations(agency: string, clientCaseId: number, data: Record<string, any>): Promise<ClientTour> {
    return this.request({
      method: 'put',
      url: this.buildPath('/v2/{agency}/case/{clientCaseId}/update-info', { agency, clientCaseId }),
      data
    });
  }

  /**
   * Archives the specified Case
   * @protected
   * @param agency
   * @param clientCase
   */
  public archiveCase(agency: string, clientCase: string | number): Promise<ClientTour> {
    return this.request({
      method: 'delete',
      url: this.buildPath('/v2/{agency}/case/{clientCase}', { agency, clientCase }),
    });
  }

  /**
   * Retrieves case data formatted for the wizard
   * @param agency
   * @param caseId
   * @protected
   */
  public getWizardData(agency: string, caseId: number): Promise<any> {
    return this.request({
      method: 'get',
      url: this.buildPath('/v2/{agency}/case/{caseId}/wizard-data', { agency, caseId }),
    });
  }

  /**
   * Retrieves a specific Case
   * @param agency
   * @param clientCase
   * @protected
   */
  public caseBuilderDetails(agency: string, clientCase: number): Promise<ClientTour> {
    return this.request({
      method: 'get',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/builder-data', { agency, clientCase }),
    });
  }

  /**
   * Creates a single case Day
   * @protected
   * @param agency
   * @param clientCase
   * @param data
   */
  public caseDayCreate(agency: string, clientCase: string | number,data: Record<string, any>): Promise<any> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/add-new-day', {
        agency,
        clientCase,
      }),
      data,
    });
  }

  /**
   * updates a single case Day
   * @protected
   * @param agency
   * @param clientCase
   * @param id
   * @param data
   */
  public caseDayUpdate(agency: string, clientCase: string | number, id: string | number, data: Record<string, any>): Promise<any> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/day/{id}', {
        agency,
        clientCase,
        id
      }),
      data,
    });
  }

  /**
   * updates order of case Day
   * @protected
   * @param agency
   * @param clientCase
   * @param id
   * @param data
   */
  public caseDayUpdateOrder(agency: string, clientCase: string | number, id: string | number, data: Record<string, any>): Promise<any> {
    return this.request({
      method: 'patch',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/day/{id}/update-day-order', {
        agency,
        clientCase,
        id
      }),
      data,
    });
  }

  /**
   * Deletes a single Case Day
   * @protected
   * @param agency
   * @param clientCase
   * @param id
   */
  public caseDayDelete(agency: string, clientCase: string | number, id: string | number): Promise<any> {
    return this.request({
      method: 'delete',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/day/{id}', {
        agency,
        clientCase,
        id
      }),
    });
  }

  /**
   * toggle case Day visibility
   * @protected
   * @param agency
   * @param caseId
   * @param caseDayId
   * @param caseDayItemId
   */
    public caseToggleItemVisibility(agency: string, caseId: string | number, caseDayId: string | number, caseDayItemId: string | number): Promise<any> {
      return this.request({
        method: 'patch',
        url: this.buildPath('/v2/{agency}/case/{caseId}/day/{caseDayId}/item/{caseDayItemId}/toggle-item', {
          agency,
          caseId,
          caseDayId,
          caseDayItemId
        }),
      });
    }

  public casePreview(caseId: string | number): Promise<any> {
    return this.request({
      method: "get",
      url: this.buildPath("/v2/public/case/{caseId}/preview", { caseId }),
    });
  }

  /**
   * Retrieves case day items
   * @protected
   * @param agency
   * @param caseId
   * @param caseDayId
   * @param caseDayItemId
   */
  public caseDayItemFetch(agency: string, caseId: string | number, caseDayId: string | number, caseDayItemId: string | number): Promise<any> {
    return this.request({
      method: 'get',
      url: this.buildPath('/v2/{agency}/case/{caseId}/day/{caseDayId}/item/{caseDayItemId}', {
        agency,
        caseId,
        caseDayId,
        caseDayItemId
      }),
    });
  }

  /**
   * Creates case day items
   * @protected
   * @param agency
   * @param clientCase
   * @param dayId
   * @param data
   */
  public caseDayItemCreate(agency: string, clientCase: string | number, dayId: string | number, data: Record<string, any>): Promise<any> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/day/{dayId}/add-new-item', {
        agency,
        clientCase,
        dayId
      }),
      data
    });
  }

  /**
   * Updates case day items
   * @protected
   * @param agency
   * @param clientCase
   * @param dayId
   * @param id
   * @param data
   */
  public caseDayItemUpdate(agency: string, clientCase: string | number, dayId: string | number, id: string | number, data: Record<string, any>): Promise<any> {
    return this.request({
      method: 'put',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/day/{dayId}/item/{id}', {
        agency,
        clientCase,
        dayId,
        id
      }),
      data
    });
  }

  /**
   * Updates case day items
   * @protected
   * @param agency
   * @param clientCase
   * @param dayId
   * @param id
   * @param data
   */
  public caseDayItemMoveAnotherDay(agency: string, clientCase: string | number, dayId: string | number, id: string | number, data: Record<string, any>): Promise<any> {
    return this.request({
      method: 'patch',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/day/{dayId}/item/{id}/move-day', {
        agency,
        clientCase,
        dayId,
        id
      }),
      data
    });
  }

  /**
   * remove case day items
   * @protected
   * @param agency
   * @param clientCase
   * @param dayId
   * @param id
   */
  public caseDayItemRemove(agency: string, clientCase: string | number, dayId: string | number, id: string | number): Promise<any> {
    return this.request({
      method: 'delete',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/day/{dayId}/item/{id}', {
        agency,
        clientCase,
        dayId,
        id
      })
    });
  }


  /**
   * update position case day item
   * @protected
   * @param agency
   * @param clientCase
   * @param dayId
   * @param id
   * @param data
   */
  public movePosition(agency: string, clientCase: string | number, dayId: string | number, id: string | number, data: Record<string, any>): Promise<any> {
    return this.request({
      method: 'patch',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/day/{dayId}/item/{id}/update-position', {
        agency,
        clientCase,
        dayId,
        id
      }),
      data
    });
  }

  /**
   * remove case day items
   * @protected
   * @param agency
   * @param clientCase
   * @param dayId
   * @param itemId
   * @param id
   */
  public caseDayItemRemoveResource(agency: string, clientCase: string | number, dayId: string | number, itemId: string | number, id: string | number): Promise<any> {
    return this.request({
      method: 'delete',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/day/{dayId}/item/{itemId}/resource/{id}', {
        agency,
        clientCase,
        dayId,
        itemId,
        id
      })
    });
  }

  /**
   * Add a pax in case
   * @protected
   * @param agency
   * @param clientCase
   * @param data
   */
  public caseAddPax(agency: string, clientCase: string | number,data: Record<string, any>): Promise<any> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/add-pax', {
        agency,
        clientCase,
      }),
      data,
    });
  }

  /**
   * Remove a pax in case
   * @protected
   * @param agency
   * @param clientCase
   * @param id
   */
  public caseRemovePax(agency: string, clientCase: string | number, id: string | number): Promise<any> {
    return this.request({
      method: 'delete',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/remove-pax/{id}', {
        agency,
        clientCase,
        id
      }),
    });
  }

  /**
   * Add case day item pax
   * @protected
   * @param agency
   * @param clientCase
   * @param dayId
   * @param data
   */
  public caseDayItemAddPax(agency: string, clientCase: string | number, dayId: string | number, data: Record<string, any>): Promise<any> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/day/{dayId}/add-pax', {
        agency,
        clientCase,
        dayId
      }),
      data
    });
  }

  /**
   * Add case day item pax
   * @protected
   * @param agency
   * @param clientCase
   * @param dayId
   * @param id
   */
  public caseDayItemRemovePax(agency: string, clientCase: string | number, dayId: string | number, id: string | number): Promise<any> {
    return this.request({
      method: 'delete',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/day/{dayId}/remove-pax/{id}', {
        agency,
        clientCase,
        dayId,
        id
      }),
    });
  }

  // case pricing service
  /**
   * Retrieves a specific Case
   * @param agency
   * @param clientCase
   * @protected
   */
  public casePricingDetails(agency: string, clientCase: number): Promise<ClientTour> {
    return this.request({
      method: 'get',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/pricing-data', { agency, clientCase }),
    });
  }

  /**
   * Update the price individual
   * @param agency
   * @param clientCase
   * @param data
   * @protected
   */
  public caseUpdatePriceIndividual(agency: string, clientCase: number, data: Record<string, any>): Promise<ClientTour> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/update-individual-price', { agency, clientCase }),
      data
    });
  }

  /**
   * Refresh resource price(s) from the supplier_resource_prices table.
   * @param agency
   * @param clientCase
   * @param data { resourceIds: number[] }
   * @protected
   */
  public caseRefreshResourcePrice(agency: string, clientCase: number, data: Record<string, any>): Promise<ClientTour> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/refresh-resource-price', { agency, clientCase }),
      data
    });
  }

  /**
   * Update the price bulk
   * @param agency
   * @param clientCase
   * @param data
   * @protected
   */
  public caseUpdatePriceBulk(agency: string, clientCase: number, data: Record<string, any>): Promise<ClientTour> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/update-bulk-price', { agency, clientCase }),
      data
    });
  }

  /**
   * Update the status individual
   * @param agency
   * @param clientCase
   * @param data
   * @protected
   */
  public caseUpdateStatusIndividual(agency: string, clientCase: number, data: Record<string, any>): Promise<ClientTour> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/update-individual-status', { agency, clientCase }),
      data
    });
  }

  /**
   * Update the status bulk
   * @param agency
   * @param clientCase
   * @param data
   * @protected
   */
  public caseUpdateStatusBulk(agency: string, clientCase: number, data: Record<string, any>): Promise<ClientTour> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/update-bulk-status', { agency, clientCase }),
      data
    });
  }

  /**
   * net validated
   * @param agency
   * @param clientCase
   * @param data
   * @protected
   */
  public caseNetValidated(agency: string, clientCase: number, data: Record<string, any>): Promise<ClientTour> {
    return this.request({
      method: 'patch',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/validate-price', { agency, clientCase }),
      data
    });
  }

  /**
   * delete bulk resources
   * @param agency
   * @param clientCase
   * @param data
   * @protected
   */
  public caseDeleteBulkResources(agency: string, clientCase: number, data: Record<string, any>): Promise<ClientTour> {
    return this.request({
      method: 'delete',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/delete-bulk-resources', { agency, clientCase }),
      data
    });
  }

  /**
   * case sold
   * @param agency
   * @param clientCase
   * @protected
   */
  public caseSold(agency: string, clientCase: number): Promise<ClientTour> {
    return this.request({
      method: 'patch',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/mark-as-sold', { agency, clientCase }),
    });
  }

  /**
   * case cancled
   * @param agency
   * @param clientCase
   * @protected
   */
  public caseCancel(agency: string, clientCase: number): Promise<ClientTour> {
    return this.request({
      method: 'patch',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/mark-as-cancelled', { agency, clientCase }),
    });
  }

  /**
   * case gross price markup price
   * @param agency
   * @param clientCase
   * @param data
   * @protected
   */
  public updateCaseGrossPriceMarkup(agency: string, clientCase: number, data: Record<string, any>): Promise<ClientTour> {
    return this.request({
      method: 'put',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/update-gross-price-markup', { agency, clientCase }),
      data
    });
  }

  /**
   * case quote price
   * @param agency
   * @param clientCase
   * @param data
   * @protected
   */
  public updateCaseQuotePrice(agency: string, clientCase: number, data: Record<string, any>): Promise<ClientTour> {
    return this.request({
      method: 'put',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/update-quote-price', { agency, clientCase }),
      data
    });
  }

  /**
   * case invoice
   * @param agency
   * @param clientCase
   * @param data
   * @protected
   */
  public caseInvoice(agency: string, clientCase: number, data: Record<string, any>): Promise<ClientTour> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/pay-via-wise', { agency, clientCase }),
      data
    });
  }

  /**
   * case invoice
   * @param agency
   * @param clientCase
   * @param data
   * @protected
   */
  public caseManualInvoice(agency: string, clientCase: number, data: Record<string, any>): Promise<ClientTour> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/add-manual-invoice', { agency, clientCase }),
      data
    });
  }

  /**
   * case invoice
   * @param agency
   * @param clientCase
   * @param data
   * @protected
   */
  public addExtraInvoice(agency: string, clientCase: number, data: Record<string, any>): Promise<ClientTour> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/extra-invoice', { agency, clientCase }),
      data
    });
  }

  /**
   * case invoice
   * @param agency
   * @param clientCase
   * @param data
   * @protected
   */
  public caseSendEmail(agency: string, clientCase: number, data: Record<string, any>): Promise<ClientTour> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/send-emails', { agency, clientCase }),
      data
    });
  }

  /**
   * Reset the Holded connection flag for selected pricing resources.
   * @param agency
   * @param clientCase
   * @param data
   */
  public caseResetHoldedConnection(agency: string, clientCase: number, data: Record<string, any>): Promise<ClientTour> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/reset-holded-connection', { agency, clientCase }),
      data
    });
  }

  /**
   * case send notification to supplier
   * @param agency
   * @param clientCase
   * @param data
   * @protected
   */
  public caseSendNotificationToSupplier(agency: string, clientCase: number, data: Record<string, any>): Promise<ClientTour> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/send-email-to-supplier', { agency, clientCase }),
      data
    });
  }

  /**
   * case send notification to client (v2 manual mail flow)
   * @param agency
   * @param clientCase
   * @param data
   * @protected
   */
  public caseSendNotificationToClient(agency: string, clientCase: number, data: Record<string, any>): Promise<any> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/send-client-notification', { agency, clientCase }),
      data
    });
  }

  /**
   * case add notes
   * @param agency
   * @param clientCase
   * @param data
   * @protected
   */
  public caseAddNotes(agency: string, clientCase: number, data: Record<string, any>): Promise<ClientTour> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/add-notes', { agency, clientCase }),
      data
    });
  }

  /**
   * case add invoice
   * @param agency
   * @param clientCase
   * @param data
   * @protected
   */
  public caseInvoices(agency: string, clientCase: number, data: Record<string, any>): Promise<ClientTour> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/add-invoice', { agency, clientCase }),
      data
    });
  }

  /**
   * case add notes
   * @param agency
   * @param clientCase
   * @param data
   * @protected
   */
  public caseInvoiceAddNotes(agency: string, clientCase: number, data: Record<string, any>): Promise<ClientTour> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/add-invoice-notes', { agency, clientCase }),
      data
    });
  }

  /**
   * case send notification to client
   * @param agency
   * @param clientCase
   * @param data
   * @protected
   */
  public caseSendNotificationClient(agency: string, clientCase: number, data: Record<string, any>): Promise<ClientTour> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/send-email-to-client-for-pax', { agency, clientCase }),
      data
    });
  }

  /**
   * sync pax case to all days
   * @param agency
   * @param clientCase
   * @param dayId
   * @protected
   */
  public syncPaxCaseAllDay(agency: string, clientCase: number, dayId: number): Promise<ClientTour> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/day/{dayId}/sync-pax', { agency, clientCase, dayId }),
    });
  }

  /**
   * copy pax from previous day
   * @param agency
   * @param clientCase
   * @param dayId
   * @protected
   */
  public copyPaxFromPreviousDay(agency: string, clientCase: number, dayId: number): Promise<ClientTour> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/day/{dayId}/copy-pax', { agency, clientCase, dayId }),
    });
  }

  /**
   * Update Case Day Item Resource
   * @protected
   * @param agency
   * @param clientCase
   * @param dayId
   * @param item
   * @param resource
   * @param data
   */
  public updateCaseDayItemResource(agency: string, clientCase: string | number, dayId: string | number, item: string | number, resource: string | number, data: Record<string, any>): Promise<any> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/day/{dayId}/item/{item}/resource/{resource}/update', {
        agency,
        clientCase,
        dayId,
        item,
        resource
      }),
      data
    });
  }

  /**
   * Update bulk Case Day Item Resource
   * @protected
   * @param agency
   * @param clientCase
   * @param dayId
   * @param item
   * @param data
   */
  public updateBulkCaseDayItemResource(agency: string, clientCase: string | number, dayId: string | number, item: string | number, data: Record<string, any>): Promise<any> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/day/{dayId}/item/{item}/update-bulk-resources', {
        agency,
        clientCase,
        dayId,
        item,
      }),
      data
    });
  }

  /**
   * Add Case Day Item Resource pax
   * @protected
   * @param agency
   * @param clientCase
   * @param dayId
   * @param item
   * @param resource
   * @param data
   */
  public addCaseDayItemResourcePax(agency: string, clientCase: string | number, dayId: string | number, item: string | number, resource: string | number, data: Record<string, any>): Promise<any> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/day/{dayId}/item/{item}/resource/{resource}/add-pax', {
        agency,
        clientCase,
        dayId,
        item,
        resource
      }),
      data
    });
  }

  /**
   * Remove Case Day Item Resource pax
   * @protected
   * @param agency
   * @param clientCase
   * @param dayId
   * @param item
   * @param resource
   * @param paxId
   */
  public removeCaseDayItemResourcePax(agency: string, clientCase: string | number, dayId: string | number, item: string | number, resource: string | number, paxId: string | number): Promise<any> {
    return this.request({
      method: 'delete',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/day/{dayId}/item/{item}/resource/{resource}/remove-pax/{paxId}', {
        agency,
        clientCase,
        dayId,
        item,
        resource,
        paxId
      }),
    });
  }

  /**
   * Retrieves a specific Case pax list
   * @param agency
   * @param clientCase
   * @protected
   */
  public casePaxList(agency: string, clientCase: number): Promise<ClientTour> {
    return this.request({
      method: 'get',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/paxes', { agency, clientCase }),
    });
  }

  /**
   * case to connect luggage
   * @param agency
   * @param clientCase
   * @protected
   */
  public caseToConnectLuggage(agency: string, clientCase: number): Promise<ClientTour> {
    return this.request({
      method: 'patch',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/connect-luggage', { agency, clientCase }),
    });
  }

  /**
   * case to connect luggage preview
   * @param agency
   * @param clientCase
   * @protected
   */
  public connectLuggagePreview(agency: string, clientCase: number): Promise<any> {
    return this.request({
      method: 'get',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/connect-luggage-preview', { agency, clientCase }),
    });
  }

  /**
   * case export as activity
   * @param agency
   * @param clientCase
   * @protected
   */
  public caseExportAsActivity(agency: string, clientCase: number): Promise<ClientTour> {
    return this.request({
      method: 'patch',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/export-as-activity', { agency, clientCase }),
    });
  }

  /**
   * case duplicate
   * @param agency
   * @param clientCase
   * @param duplicateCase
   * @protected
   */
  public caseDuplicate(agency: string, clientCase: number, duplicateCase: number): Promise<ClientTour> {
    return this.request({
      method: 'patch',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/duplicate-case/{duplicateCase}', { agency, clientCase, duplicateCase }),
    });
  }

  /**
   * case copy
   * @param agency
   * @param clientCase
   * @protected
   */
  public caseCopy(agency: string, clientCase: number): Promise<ClientTour> {
    return this.request({
      method: 'patch',
      url: this.buildPath('/v2/{agency}/case/{clientCase}/copy-case', { agency, clientCase }),
    });
  }

  /**
   * accept email
   * @protected
   * @param caseId
   * @param token
   * @param data
   */
  public acceptEmail(caseId: string | number, token: any, data: any): Promise<any[]> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/public/accept/{caseId}/{token}', { caseId, token }),
      data
    });
  }

  /**
   * decline email
   * @protected
   * @param caseId
   * @param token
   * @param data
   */
  public declineEmail(caseId: string | number, token: any, data: any): Promise<any[]> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/public/decline/{caseId}/{token}', { caseId, token }),
      data
    });
  }

  /**
   * fetch data for modification
   * @protected
   * @param caseId
   * @param token
   * @param data
   */
  public fetchModification(caseId: string | number, token: any, data: any): Promise<any[]> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/public/fetch/{caseId}/{token}', { caseId, token }),
      data
    });
  }

  /**
   * modification email
   * @protected
   * @param caseId
   * @param token
   * @param data
   */
  public modificationEmail(caseId: string | number, token: any, data: any): Promise<any[]> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/public/modify/{caseId}/{token}', { caseId, token }),
      data
    });
  }

  /**
   * modification email
   * @protected
   * @param agency
   * @param params
   */
  public dropdown(agency: string, params?: any): Promise<any[]> {
    return this.request({
      method: 'get',
      url: this.buildPath('/v2/{agency}/cases-dropdown', { agency }),
      params
    });
  }

  /**
   * case quote
   * @protected
   * @param agency
   * @param caseId
   */
  public caseQuote(agency: string, caseId: string | number): Promise<any[]> {
    return this.request({
      method: 'get',
      url: this.buildPath('/v2/{agency}/case/{caseId}/quote', { agency, caseId })
    });
  }

  /**
   * send quote
   * @protected
   * @param agency
   * @param caseId
   * @param data
   */
  public sendQuote(agency: string, caseId: string | number, data: any): Promise<any[]> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/case/{caseId}/quote', { agency, caseId }),
      data
    });
  }

  /**
   * update quote
   * @protected
   * @param agency
   * @param caseId
   * @param data
   */
  public updateQuote(agency: string, caseId: string | number, data: any): Promise<any[]> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/case/{caseId}/quote/update', { agency, caseId }),
      data
    });
  }

  /**
   * Add a pax in case
   * @protected
   * @param agency
   * @param paxId
   * @param data
   */
  public updatePax(agency: string, paxId: string | number,data: Record<string, any>): Promise<any> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/pax/{paxId}/update', {
        agency,
        paxId,
      }),
      data,
    });
  }

  public updateCaseFromWizard(agency: string, caseId: number, data: Record<string, any>): Promise<any> {
    return this.request({
      method: 'put',
      url: this.buildPath('/v2/{agency}/case/{caseId}/wizard', { agency, caseId }),
      data,
    });
  }

  /**
   * Get quote summary for public quote-confirm page
   * @public
   * @param caseId
   * @param token
   */
  public getQuoteSummary(caseId: string | number, token: string): Promise<any> {
    return this.request({
      method: 'get',
      url: this.buildPath('/v2/public/quote/{caseId}/summary', { caseId }),
      params: { token },
    });
  }

  /**
   * Accept quote (public, converts precase to case)
   * @public
   * @param caseId
   * @param token
   */
  public acceptQuote(caseId: string | number, token: string): Promise<any> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/public/quote/{caseId}/accept', { caseId }),
      data: { token },
    });
  }

  /**
   * Get quote history for a case
   * @protected
   * @param agency
   * @param caseId
   */
  public quoteHistory(agency: string, caseId: string | number): Promise<any> {
    return this.request({
      method: 'get',
      url: this.buildPath('/v2/{agency}/case/{caseId}/quote/history', { agency, caseId }),
    });
  }

  /**
   * Get email notification logs (for setup page)
   * @protected
   * @param agency
   * @param params
   */
  public emailLogs(agency: string, params?: any): Promise<any> {
    return this.request({
      method: 'get',
      url: this.buildPath('/v2/{agency}/email-logs', { agency }),
      params,
    });
  }
}
